import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  X,
  Check,
  Copy,
  Radio,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  FetchRawLogsQuery,
  type RawLogSearchInput,
  type IngestionEvent,
  type IngestionFilter,
} from '@/lib/api/system/ingestion-observability';
import { cn } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { IngestionEventTrace } from './IngestionEventTrace';
import { OutcomeIcon } from './OutcomeIcon';
import { useIngestionManifest } from '../hooks/useIngestionManifest';

const PAGE_WINDOW_MINUTES = 15;
const MAX_ACCUMULATED_EVENTS = 500;

function eventKey(e: IngestionEvent): string {
  return `${e.correlationId}-${e.stage}-${e.timestamp}`;
}

interface IngestionLiveLogsProps {
  tenantId: string;
  lockedFilters?: Partial<IngestionFilter>;
}

export function IngestionLiveLogs({ tenantId, lockedFilters }: IngestionLiveLogsProps) {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedTrace, setSelectedTrace] = useState<{ correlationId: string; timestamp: string } | null>(null);

  // Filters
  const [stage, setStage] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);

  // Time-cursor pagination: `until` is the upper bound of the current page.
  // null = "now" (latest), meaning auto-refresh controls the upper bound.
  const [until, setUntil] = useState<string | null>(null);

  const { stageOptions, outcomeOptions, allTypeOptions, getTypesForStage, stageDisplayNames } = useIngestionManifest();

  const typeOptions = stage
    ? getTypesForStage(stage).map((t) => ({ value: t, label: t }))
    : allTypeOptions;

  // Accumulated events across polls and pagination, sorted newest-first
  const [events, setEvents] = useState<IngestionEvent[]>([]);
  const seenKeys = useRef(new Set<string>());

  // Track filter identity so we can reset accumulation when filters change
  const filterKey = `${stage}|${outcome}|${type}|${correlationId}`;
  const prevFilterKey = useRef(filterKey);

  const input: RawLogSearchInput = useMemo(() => {
    const base: RawLogSearchInput = {
      stage: stage ?? undefined,
      outcome: outcome ?? undefined,
      type: type ?? undefined,
      correlationId: correlationId ?? undefined,
      projectId: lockedFilters?.projectId ?? undefined,
      videoProcessorConfigId: lockedFilters?.videoProcessorConfigId ?? undefined,
    };
    if (until) {
      base.until = until;
      base.since = new Date(new Date(until).getTime() - PAGE_WINDOW_MINUTES * 60 * 1000).toISOString();
    }
    return base;
  }, [stage, outcome, type, correlationId, until, lockedFilters]);

  const isAtLatest = until === null;

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    ...FetchRawLogsQuery(tenantId, input),
    refetchInterval: autoRefresh && isAtLatest ? 15000 : false,
  });

  // Merge new results into accumulated list (also handles filter resets)
  useEffect(() => {
    if (!data) return;

    // Reset accumulation when filters change
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      seenKeys.current.clear();
      setEvents([]);
    }

    const newEvents: IngestionEvent[] = [];
    for (const e of data) {
      const key = eventKey(e);
      if (!seenKeys.current.has(key)) {
        seenKeys.current.add(key);
        newEvents.push(e);
      }
    }
    if (newEvents.length === 0) return;

    setEvents((prev) => {
      const merged = [...prev, ...newEvents]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      // Cap to avoid unbounded growth
      if (merged.length > MAX_ACCUMULATED_EVENTS) {
        const dropped = merged.slice(MAX_ACCUMULATED_EVENTS);
        for (const e of dropped) seenKeys.current.delete(eventKey(e));
        return merged.slice(0, MAX_ACCUMULATED_EVENTS);
      }
      return merged;
    });
  }, [data, filterKey]);

  const hasFilters = !!(stage || outcome || type || correlationId);

  const goOlder = useCallback(() => {
    if (events.length > 0) {
      const oldest = events[events.length - 1];
      setUntil(oldest.timestamp);
    } else if (until) {
      setUntil(new Date(new Date(until).getTime() - PAGE_WINDOW_MINUTES * 60 * 1000).toISOString());
    }
  }, [events, until]);

  const goNewer = useCallback(() => {
    if (!until) return;
    const cursorTime = new Date(until).getTime();
    const now = Date.now();
    const newUntil = cursorTime + PAGE_WINDOW_MINUTES * 60 * 1000;
    if (newUntil >= now - 30_000) {
      setUntil(null);
    } else {
      setUntil(new Date(newUntil).toISOString());
    }
  }, [until]);

  const jumpToLatest = useCallback(() => {
    setUntil(null);
  }, []);

  return (
    <div className="space-y-3">
      {/* Filters + auto-refresh controls */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#2E3835] bg-white/[0.02] px-3 py-1.5">
        <Combobox
          options={outcomeOptions}
          value={outcome ?? undefined}
          onSelect={(v) => setOutcome(v || null)}
          placeholder="Outcome"
          searchPlaceholder="Search..."
          className="w-[160px] [&_button]:border-[#2E3835] [&_button]:bg-transparent [&_button]:text-sm [&_button]:text-white/60 [&_button]:shadow-none"
        />
        <Combobox
          options={stageOptions}
          value={stage ?? undefined}
          onSelect={(v) => {
            setStage(v || null);
            setType(null);
          }}
          placeholder="Stage"
          searchPlaceholder="Search stages..."
          className="w-[280px] [&_button]:border-[#2E3835] [&_button]:bg-transparent [&_button]:text-sm [&_button]:text-white/60 [&_button]:shadow-none"
        />
        {typeOptions.length > 0 && (
          <Combobox
            options={typeOptions}
            value={type ?? undefined}
            onSelect={(v) => setType(v || null)}
            placeholder="Type"
            searchPlaceholder="Search types..."
            className="w-[160px] [&_button]:border-[#2E3835] [&_button]:bg-transparent [&_button]:text-sm [&_button]:text-white/60 [&_button]:shadow-none"
          />
        )}
        <input
          value={correlationId ?? ''}
          onChange={(e) => setCorrelationId(e.target.value || null)}
          placeholder="Correlation ID"
          className="h-8 w-[220px] rounded-md border border-[#2E3835] bg-transparent px-2 text-sm text-white/60 placeholder:text-white/30 outline-none focus:border-white/20"
        />
        {hasFilters && (
          <button
            onClick={() => {
              setStage(null);
              setOutcome(null);
              setType(null);
              setCorrelationId(null);
            }}
            className="flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/60 transition-colors hover:bg-white/20 hover:text-white"
          >
            <X className="h-2.5 w-2.5" />
            Clear
          </button>
        )}

        <div className="flex-1" />

        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors',
            autoRefresh
              ? 'bg-green-500/15 text-green-400'
              : 'border border-emerald-500/30 text-emerald-400/70 hover:bg-emerald-500/10 hover:text-emerald-400'
          )}
        >
          <Radio className={cn('h-3 w-3', autoRefresh && 'animate-pulse')} />
          {autoRefresh ? 'Auto-refresh on' : 'Auto-refresh off'}
        </button>
      </div>

      {/* Content */}
      <div className="flex gap-3">
        <div className={cn('min-w-0 flex-1 space-y-2', selectedTrace && 'max-w-[60%]')}>
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2 text-xs text-red-400">
              Failed to load logs: {error.message}
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-[#2E3835] bg-[#1a1a1a]">
            {/* Load newer button */}
            {!isAtLatest && !autoRefresh && (
              <button
                onClick={goNewer}
                className="flex w-full items-center justify-center gap-1.5 border-b border-[#2E3835] bg-white/[0.02] py-1.5 text-[10px] text-white/40 transition-colors hover:bg-white/[0.04] hover:text-white/60"
              >
                <ChevronUp className="h-3 w-3" />
                Load newer logs
              </button>
            )}

            {/* Jump to latest when paginated back */}
            {!isAtLatest && autoRefresh && (
              <button
                onClick={jumpToLatest}
                className="flex w-full items-center justify-center gap-1.5 border-b border-[#2E3835] bg-emerald-500/5 py-1.5 text-[10px] text-emerald-400/60 transition-colors hover:bg-emerald-500/10 hover:text-emerald-400"
              >
                <Radio className="h-3 w-3" />
                Jump to latest
              </button>
            )}

            {/* Table header */}
            <div className="flex items-center gap-0 border-b border-[#2E3835] bg-white/[0.02] text-[10px] font-medium uppercase tracking-wider text-white/30">
              <div className="w-[32px] shrink-0 px-1.5 py-1.5" />
              <div className="w-[140px] shrink-0 px-1 py-1.5">Time</div>
              <div className="w-[170px] shrink-0 px-1 py-1.5">Stage</div>
              <div className="w-[80px] shrink-0 px-1 py-1.5">Type</div>
              <div className="w-[100px] shrink-0 px-1 py-1.5">Site</div>
              <div className="w-[180px] shrink-0 px-1 py-1.5">Correlation</div>
              <div className="min-w-0 flex-1 px-1 py-1.5">Reason</div>
            </div>

            {/* Rows */}
            {isLoading && events.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-4 w-4 animate-spin text-white/30" />
              </div>
            ) : events.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-xs text-white/30">
                {isAtLatest
                  ? 'No raw logs found in the last 15 minutes'
                  : 'No logs found in this time window'}
              </div>
            ) : (
              <div className="divide-y divide-white/[0.03]">
                {events.map((event, i) => (
                  <RawLogRow
                    key={`${event.correlationId}-${event.stage}-${i}`}
                    event={event}
                    onTraceClick={setSelectedTrace}
                    isSelected={event.correlationId === selectedTrace?.correlationId}
                    stageDisplayNames={stageDisplayNames}
                  />
                ))}
              </div>
            )}

            {/* Load older button */}
            {events.length > 0 && (
              <button
                onClick={goOlder}
                className="flex w-full items-center justify-center gap-1.5 border-t border-[#2E3835] bg-white/[0.02] py-1.5 text-[10px] text-white/40 transition-colors hover:bg-white/[0.04] hover:text-white/60"
              >
                <ChevronDown className="h-3 w-3" />
                Load older logs
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 rounded-lg border border-[#2E3835] bg-white/[0.02] px-3 py-1.5">
            <div className="flex items-center gap-2 text-[10px] text-white/40">
              <span>
                {events.length} raw log{events.length !== 1 ? 's' : ''} accumulated
                {events.length >= MAX_ACCUMULATED_EVENTS && ' (capped)'}
              </span>
              {dataUpdatedAt > 0 && (
                <span className="text-white/25">
                  last fetched {new Date(dataUpdatedAt).toLocaleTimeString()}
                </span>
              )}
            </div>
            {!isAtLatest && (
              <button
                onClick={jumpToLatest}
                className="flex items-center gap-1 text-[10px] text-emerald-400/60 transition-colors hover:text-emerald-400"
              >
                <Radio className="h-3 w-3" />
                Jump to latest
              </button>
            )}
          </div>
        </div>

        {/* Trace side panel */}
        {selectedTrace && (
          <div className="sticky top-16 max-h-[85vh] w-[40%] min-w-[380px] overflow-auto rounded-lg border border-[#2E3835] bg-[#1a1a1a] p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-medium text-white">Event Trace</h3>
              <button
                onClick={() => setSelectedTrace(null)}
                className="text-white/40 hover:text-white/80"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <IngestionEventTrace tenantId={tenantId} correlationId={selectedTrace.correlationId} timestampHint={selectedTrace.timestamp} />
          </div>
        )}
      </div>
    </div>
  );
}

// --- Metadata pill ---

function MetadataPill({ label, variant = 'neutral' }: { label: string; variant?: 'neutral' | 'error' }) {
  return (
    <span
      title={label}
      className={cn(
        'inline-block max-w-[150px] truncate rounded px-1.5 py-0.5 text-[9px]',
        variant === 'error'
          ? 'bg-red-500/10 text-red-400/60'
          : 'bg-white/5 text-white/30'
      )}
    >
      {label}
    </span>
  );
}

// --- CopyableId ---

function CopyableId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      title={id}
      className="group/copy inline-flex items-center gap-1 font-mono text-white/40 transition-colors hover:text-white/70"
    >
      <span>{id.slice(0, 8)}</span>
      {copied ? (
        <Check className="h-2.5 w-2.5 text-green-400" />
      ) : (
        <Copy className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover/copy:opacity-100" />
      )}
    </button>
  );
}

// --- EntityCell: shows copyable ID or dash ---

function EntityCell({ id }: { id?: string }) {
  if (!id) return <span className="text-white/15">—</span>;
  return <CopyableId id={id} />;
}

// --- RawLogRow ---

function RawLogRow({
  event,
  onTraceClick,
  isSelected,
  stageDisplayNames,
}: {
  event: IngestionEvent;
  onTraceClick: (trace: { correlationId: string; timestamp: string }) => void;
  isSelected: boolean;
  stageDisplayNames: Map<string, string>;
}) {
  const pills: { label: string; variant: 'neutral' | 'error' }[] = [];
  if (event.metadataError) pills.push({ label: event.metadataError, variant: 'error' });
  if (event.stopReason) pills.push({ label: `Stop: ${event.stopReason}`, variant: 'error' });
  if (event.metadataEventType && pills.length < 2) pills.push({ label: event.metadataEventType, variant: 'neutral' });
  if (event.metadataVendor && pills.length < 2) pills.push({ label: event.metadataVendor, variant: 'neutral' });
  if (event.metadataTriggerType && pills.length < 2) pills.push({ label: event.metadataTriggerType, variant: 'neutral' });

  return (
    <div
      onClick={() => event.correlationId && onTraceClick({ correlationId: event.correlationId, timestamp: event.timestamp })}
      className={cn(
        'flex items-center gap-0 border-l-2 px-0 py-0.5 text-[11px] transition-colors hover:bg-white/[0.02]',
        event.correlationId ? 'cursor-pointer' : '',
        isSelected ? 'border-l-blue-400/60 bg-blue-500/[0.08]' : 'border-l-transparent'
      )}
    >
      {/* Outcome icon */}
      <span className="w-[32px] shrink-0 flex items-center justify-center px-1">
        <OutcomeIcon outcome={event.outcome} />
      </span>

      {/* Time */}
      <span className="w-[140px] shrink-0 px-1 font-mono text-white/60">
        {new Date(event.timestamp).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </span>

      {/* Stage */}
      <span className="w-[170px] shrink-0 truncate px-1 font-medium text-white/80">
        {stageDisplayNames.get(event.stage) ?? event.stage}
      </span>

      {/* Type */}
      <span className="w-[80px] shrink-0 truncate px-1 text-white/40">
        {event.type || <span className="text-white/15">—</span>}
      </span>

      {/* Site */}
      <span className="w-[100px] shrink-0 truncate px-1">
        <EntityCell id={event.projectId} />
      </span>

      {/* Correlation ID */}
      <span className="w-[180px] shrink-0 px-1">
        {event.correlationId ? (
          <CopyableId id={event.correlationId} />
        ) : (
          <span className="text-white/15">—</span>
        )}
      </span>

      {/* Reason + metadata pills */}
      <span className="min-w-0 flex-1 flex items-center gap-1.5 px-1 overflow-hidden">
        {event.reason && (
          <span className="truncate text-white/35" title={event.reason}>
            {event.reason}
          </span>
        )}
        {pills.map((pill, i) => (
          <MetadataPill key={i} label={pill.label} variant={pill.variant} />
        ))}
      </span>
    </div>
  );
}
